import { Tooltip } from "components/Tooltip";
import { tooltips } from "./tooltips";
import { AdditionalOptionsListbox } from "./AdditionalOptionsListBox";
import type { AsyncApiParams, CollectorConfig } from "providers/HostedScrapingProvider/types";
import { URLSearchParamsInit, useSearchParams } from "react-router-dom";
import { useEffect } from "react";

type ParseResultsDropdownProps = {
  collectorConfig: CollectorConfig;
  updateApiParams: (key: string) => (newValue: string | boolean | number | undefined, apiParamsToChange?: AsyncApiParams) => AsyncApiParams;
  skipExplanation?: boolean;
  scrapingVariant: string;
};
const ParseResultsDropdown = ({ collectorConfig, updateApiParams, skipExplanation = false, scrapingVariant }: ParseResultsDropdownProps) => {

  const [, setSearchParams] = useSearchParams();

  const isLlmProject = collectorConfig.type === 'async_urls_llm';

  const options = [
    { text: 'Do not parse results', value: 'do_not_parse' },
    { text: skipExplanation ? 'Parse to JSON' : 'Parse to JSON (Only available for certain domains)', value: 'json' },
    { text: skipExplanation ? 'Parse to CSV' : 'Parse to CSV (Only available for certain domains)', value: 'csv' },
    { text: skipExplanation ? 'Parse to Markdown' : 'Parse to Markdown (get LLM-ready data, available for all domains)', value: 'markdown' },
    { text: skipExplanation ? 'Parse to Text' : 'Parse to Text (get LLM-ready data, available for all domains)', value: 'text' },
  ];

  const llmOptions = [
    { text: skipExplanation ? 'Parse to Markdown' : 'Parse to Markdown (get LLM-ready data, available for all domains)', value: 'markdown' },
    { text: skipExplanation ? 'Parse to Text' : 'Parse to Text (get LLM-ready data, available for all domains)', value: 'text' },
  ]

const selectedValue = (() => {
  const { autoparse, outputFormat } = collectorConfig.apiParams || {};

  if (isLlmProject) {
    return outputFormat === "text" ? "text" : "markdown";
  }

  if (autoparse && outputFormat === "csv") return "csv";
  if (autoparse) return "json";
  if (outputFormat === "markdown" || outputFormat === "text") return outputFormat;

  return "do_not_parse";
})();

  const config: Record<string, { outputFormat?: string; autoparse: boolean | undefined }> = {
    do_not_parse: { autoparse: false },
    json: { outputFormat: 'json', autoparse: true },
    csv: { outputFormat: 'csv', autoparse: true },
    markdown: { outputFormat: 'markdown', autoparse: undefined },
    text: { outputFormat: 'text', autoparse: undefined},
  };

  const handleChange = (new_value: string | undefined) => {
    if (!new_value) return;
  
    const setting = config[new_value];

    if (!setting) return;
  
    if (scrapingVariant === 'api-playground') {
      const { apiParams, urlParams } = updateApiParams('outputFormat')(setting.outputFormat);
      updateApiParams('autoparse')(setting.autoparse, apiParams as AsyncApiParams);
      setSearchParams(urlParams as URLSearchParamsInit);
    } else { // for the data pipeline
      const apiParams = updateApiParams('outputFormat')(setting.outputFormat);
      updateApiParams('autoparse')(setting.autoparse, apiParams as AsyncApiParams);
    }
  };

  useEffect(() => {
    // set default value of markdown.
    if (isLlmProject && collectorConfig.apiParams?.outputFormat === undefined) {
      const apiParams = updateApiParams("outputFormat")("markdown");
      updateApiParams("autoparse")(undefined, apiParams as AsyncApiParams);
    }
  }, [isLlmProject, collectorConfig.apiParams?.outputFormat, updateApiParams]);

  return <>
    <div className="mt-5">
      <Tooltip content={isLlmProject ? tooltips.llmParsing : tooltips.autoparse}>{isLlmProject ? 'Output formats' : 'Parse results'}</Tooltip>
    </div>
    <AdditionalOptionsListbox 
      value={ selectedValue } 
      options={ isLlmProject ? llmOptions : options } 
      callback={(new_value) => handleChange(new_value)} 
      shortList
      shorterList={isLlmProject} 
      buttonTestId="testParseResults" 
    />
  </>
};
export default ParseResultsDropdown;

